import React, { useState, useEffect } from 'react';
import {
    Container, Col, Row,
    Modal, ModalHeader, ModalBody, ModalFooter,
    Form, FormGroup, Label, Input, Button
} from 'reactstrap';
import { RiAlarmWarningFill, RiToolsFill, RiFileChartLine } from "react-icons/ri";
import AntivirusPatchReport from './AntivirusPatchReport';
import { Link, useNavigate } from 'react-router-dom';
import { hasFeature } from '../../helpers/features';

const ORANGE_COLORS = {
    primary: '#FF5B00',
    dark:    '#e85a28',
};
const ORANGE        = ORANGE_COLORS.primary;
const ORANGE_BORDER = 'rgba(255, 91, 0, 0.2)';


const pageHeadingStyle = {
  color: "var(--vz-body-color)",
};

const pageIconWrapStyle = {
  width: 32,
  height: 32,
  borderRadius: 8,
  border: "1px solid rgba(var(--vz-primary-rgb), 0.35)",
  background: "rgba(var(--vz-primary-rgb), 0.08)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const pageIconStyle = {
  color: "var(--vz-primary)",
  fontSize: 16,
};


// ── Reusable report card ──────────────────────────────────────────────────────
const ReportCard = ({ icon: Icon, title, description, badge, onClick, to }) => {
    const [hovered, setHovered] = useState(false);

    const inner = (
        <div
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
             style={{
                background: "var(--vz-secondary-bg, #fff)",
                border: `1px solid ${hovered ? ORANGE_BORDER : "var(--vz-border-color)"}`,
                borderRadius: 10,
                padding: '28px 28px 24px',
                transition: 'all 0.22s ease',
                cursor: 'pointer',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                boxShadow: hovered
                    ? '0 8px 18px rgba(0,0,0,0.08)'
                    : '0 1px 4px rgba(0,0,0,0.06)',
                transform: hovered ? 'translateY(-2px)' : 'translateY(0)',
            }}
        >
            {/* Icon + badge row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{
                    width: 48, height: 48, borderRadius: '12px',
                    backgroundColor: `${ORANGE}15`,
                    border: `1px solid ${ORANGE_BORDER}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                }}>
                    <Icon size={22} color={ORANGE} />
                </div>
                {badge && (
                    <span style={{
                        fontSize: '10px', fontWeight: 700, textTransform: 'uppercase',
                        letterSpacing: '0.5px', padding: '3px 8px', borderRadius: '20px',
                        backgroundColor: `${ORANGE}15`, color: ORANGE,
                        border: `1px solid ${ORANGE_BORDER}`,
                    }}>
                        {badge}
                    </span>
                )}
            </div>

            {/* Text */}
            <div style={{ flex: 1 }}>
                <h5 className="mb-1" style={{ fontSize: '15px', fontWeight: 700 }}>
                    {title}
                </h5>
                <p className="text-muted mb-0" style={{ fontSize: '13px', lineHeight: 1.55 }}>
                    {description}
                </p>
            </div>

            {/* CTA */}
            <div className="text-center">
                <button className="rounded btn btn-outline-primary">
                    View Details
                </button>
            </div>
        </div>
    );

    if (to) return (
        <Link to={to} style={{ textDecoration: 'none', display: 'block', height: '100%' }}>
            {inner}
        </Link>
    );
    return <div onClick={onClick} style={{ height: '100%' }}>{inner}</div>;
};

// ── Main component ────────────────────────────────────────────────────────────
function Report() {
    const [modalOpen, setModalOpen]       = useState(false);
    const [agent, setAgent]               = useState('');
    const [days, setDays]                 = useState('');
    const [agentdetails, setAgentdetails] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchAgents = async () => {
            try {
                const authUser = JSON.parse(sessionStorage.getItem('authUser'));
                const headers  = {
                    'Content-Type': 'application/json',
                    ...(authUser?.token && { Authorization: `Bearer ${authUser.token}` }),
                };
                const response = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}/threats/agents`,
                    { headers }
                );
                const data = await response.json();
                if (data.success && data.agents) setAgentdetails(data.agents);
            } catch (error) {
                console.error('Error fetching agents:', error);
            }
        };
        fetchAgents();
    }, []);

    const handleGenerateReport = () => {
        if (!agent || !days) {
            alert('Please select agent and duration');
            return;
        }
        navigate(`/agent-report/${agent}?days=${days}`);
    };

    return (
        <div className="page-content mt-5 pt-4">
            <Container fluid>

                {/* ── Page header — matches app-wide pattern ── */}
                <Row className="mb-4 mt-2">
                    <Col>
                        <h4 className="mb-0" style={pageHeadingStyle}>
                             <div style={pageIconWrapStyle} className='me-2'>
                                 <i className="ri-file-chart-line" style={pageIconStyle}></i>
                             </div>
                           
                            Reports
                        </h4>
                        <p className="text-muted mb-0 mt-2" style={{ fontSize: '13px' }}>
                            Generate and export security reports for your environment
                        </p>
                    </Col>
                </Row>

                {/* ── Report cards ── */}
                <Row className="gy-4">
                    <Col md={6}>
                        <ReportCard
                            icon={RiAlarmWarningFill}
                            title="Threats Report"
                            description="Shows agent-based threats detected in the environment with severity levels and timeline analysis."
                            badge="Threats"
                            onClick={() => setModalOpen(true)}
                        />
                    </Col>
                    {hasFeature('av') && (
                        <Col md={6}>
                            <ReportCard
                                icon={RiToolsFill}
                                title="Vulnerability Report"
                                description="Lists critical vulnerabilities found across assets and agents with remediation priorities."
                                badge="Vulns"
                                to="/vulnerability-report"
                            />
                        </Col>
                    )}
                </Row>

                {/* ── AntivirusPatchReport ── */}
                {hasFeature('av') && (
                    <div className="mt-4">
                        <AntivirusPatchReport />
                    </div>
                )}

            </Container>

            {/* ── Modal ── */}
            <Modal
                isOpen={modalOpen}
                toggle={() => setModalOpen(false)}
                centered
                modalClassName="fadeInRight"
            >
                <ModalHeader
                    toggle={() => setModalOpen(false)}
                    style={{ borderBottom: `1px solid ${ORANGE_BORDER}`, padding: '18px 24px' }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{
                            width: 34, height: 34, borderRadius: '8px',
                            backgroundColor: `${ORANGE}15`,
                            border: `1px solid ${ORANGE_BORDER}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <RiFileChartLine size={18} color={ORANGE} />
                        </div>
                        <div>
                            <div style={{ fontSize: '15px', fontWeight: 700, lineHeight: 1.2 }}>
                                Threat Analysis Report
                            </div>
                            <div className="text-muted" style={{ fontSize: '11px', marginTop: '1px' }}>
                                Select agent and time range
                            </div>
                        </div>
                    </div>
                </ModalHeader>

                <ModalBody style={{ padding: '24px' }}>
                    <Form>
                        <FormGroup style={{ marginBottom: '20px' }}>
                            <Label className="text-muted" style={{
                                fontSize: '11px', fontWeight: 700,
                                textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px'
                            }}>
                                Agent
                            </Label>
                            <Input
                                type="select"
                                value={agent}
                                onChange={e => setAgent(e.target.value)}
                                style={{
                                    fontSize: '13px', borderRadius: '8px',
                                    border: `1px solid ${agent ? ORANGE_BORDER : 'rgba(128,128,128,0.25)'}`,
                                    padding: '9px 12px', boxShadow: 'none',
                                    transition: 'border-color 0.15s ease',
                                }}
                            >
                                <option value="">— Select agent —</option>
                                <option value="All">All Agents</option>
                                {agentdetails.map((a, i) => (
                                    <option key={i} value={a}>{a}</option>
                                ))}
                            </Input>
                        </FormGroup>

                        <FormGroup style={{ marginBottom: 0 }}>
                            <Label className="text-muted" style={{
                                fontSize: '11px', fontWeight: 700,
                                textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px'
                            }}>
                                Duration
                            </Label>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                {[
                                    { label: '1 Week',  value: '7'     },
                                    { label: '1 Month', value: '30'    },
                                    { label: 'Any',     value: 'other' },
                                ].map(opt => (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => setDays(opt.value)}
                                        style={{
                                            flex: 1, padding: '9px 0',
                                            borderRadius: '8px', fontSize: '13px', fontWeight: 500,
                                            border: `1px solid ${days === opt.value ? ORANGE : 'rgba(128,128,128,0.25)'}`,
                                            backgroundColor: days === opt.value ? `${ORANGE}12` : 'transparent',
                                            color: days === opt.value ? ORANGE : 'inherit',
                                            cursor: 'pointer', transition: 'all 0.15s ease',
                                        }}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </FormGroup>
                    </Form>
                </ModalBody>

                <ModalFooter style={{ borderTop: `1px solid ${ORANGE_BORDER}`, padding: '16px 24px', gap: '8px' }}>
                    <Button
                        onClick={() => setModalOpen(false)}
                        style={{
                            backgroundColor: 'transparent',
                            border: '1px solid rgba(128,128,128,0.25)',
                            color: 'inherit', borderRadius: '8px',
                            fontSize: '13px', padding: '8px 18px', fontWeight: 500,
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleGenerateReport}
                        disabled={!agent || !days}
                        style={{
                            backgroundColor: agent && days ? ORANGE : 'rgba(128,128,128,0.15)',
                            border: 'none',
                            color: agent && days ? '#fff' : 'inherit',
                            borderRadius: '8px', fontSize: '13px',
                            padding: '8px 20px', fontWeight: 600,
                            transition: 'all 0.15s ease',
                            display: 'flex', alignItems: 'center', gap: '6px',
                            opacity: agent && days ? 1 : 0.5,
                        }}
                    >
                        <RiFileChartLine size={15} />
                        Generate Report
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
}

export default Report;